import React, { useCallback, useEffect, useState } from 'react';
import ProgramManagerModalContent from './ProgramManagerModalContent';
import TrueImpactSimpleModal from '../../design-system/components/TrueImpactSimpleModal';
import { ProgramManager, Report } from './utils';
import { useClient } from '../../apollo';
import { updateProgram } from './api';
import { useGraphQLRequest } from '../../auth/hooks/useApiRequest';

const getInitialManager = (
  options: ProgramManager[],
  currentProgramManager: ProgramManager | undefined,
) => {
  if (!currentProgramManager) return undefined;
  const result = options.find(x => x.email === currentProgramManager.email);
  return result || currentProgramManager;
};

interface ProgramManagerModalProps {
  open: boolean;
  options: ProgramManager[];
  reportId: Report['dbId'];
  programId: Report['programId'];
  currentProgramManager: ProgramManager | undefined;
  onClose: () => unknown;
  handleCurrentManagerChange: (manager: ProgramManager) => unknown;
}

const ProgramManagerModal: React.FC<ProgramManagerModalProps> = ({
  open,
  options,
  reportId,
  programId,
  currentProgramManager,
  handleCurrentManagerChange,
  onClose,
}) => {
  const client = useClient();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requestState, makeRequest] = useGraphQLRequest<any>();
  const [manager, setManager] = useState<ProgramManager | undefined>(
    getInitialManager(options, currentProgramManager),
  );

  const handleClose = useCallback(() => {
    setManager(getInitialManager(options, currentProgramManager));
    onClose();
  }, [onClose]);

  useEffect(() => {
    if (!currentProgramManager) return;
    setManager(getInitialManager(options, currentProgramManager));
  }, [currentProgramManager]);

  const handleManagerChange = useCallback((manager: ProgramManager) => {
    setManager(manager);
  }, []);

  useEffect(() => {
    if (!manager || requestState.type !== 'REQUEST_SUCCESS') return;
    handleCurrentManagerChange(manager);
    onClose();
  }, [requestState.type]);

  const handleSave = useCallback(() => {
    if (!manager) return;
    makeRequest(updateProgram(client!, programId, manager.userId));
  }, [manager, programId]);

  return (
    <TrueImpactSimpleModal
      open={open}
      onClose={onClose}
      next={{
        text: 'Save',
        isLoading: requestState.type === 'REQUEST_START',
        onClick: handleSave,
      }}
      previous={{
        text: 'Cancel',
        disabled: requestState.type === 'REQUEST_START',
        onClick: handleClose,
      }}
    >
      <ProgramManagerModalContent
        options={options}
        manager={manager}
        errorText={
          requestState.type === 'REQUEST_ERROR'
            ? requestState.error.message || 'Unable to program manager'
            : undefined
        }
        currentProgramManager={currentProgramManager}
        handleManagerChange={handleManagerChange}
      />
    </TrueImpactSimpleModal>
  );
};

export default ProgramManagerModal;
